<template>
    <div class="box p-15">
        <div class="columns is-mobile">
            <div class="column is-4">
                <component :is="isDeclined ? 'span' : 'a'" class="image campaign-image is-4by3" @click="goToActivation">
                    <!-- Could replace with GThumbnailComponent, but needs to match CampaignListRow -->
                    <img class="activation-image" :src="activationImage" :alt="brandName" />
                </component>
            </div>
            <div class="column is-8">
                <div class="title is-5 campaign-title">
                    {{ activation.public_name }}
                </div>
                <div v-if="activation.description" class="text-14 mtb-5 text-light break-new-line">
                    <div :class="descriptionClass" @click="toggleShowDescription">
                        <div v-html="activation.description"></div>
                    </div>
                </div>
                <div class="mtb-10">
                    <g-level>
                        <g-level-item>
                            <router-link v-if="!isDeclined" :to="continueRoute" class="no-underline">
                                <button class="button">
                                    {{ continueText }}
                                </button>
                            </router-link>
                        </g-level-item>
                        <g-level-item slot="right">
                            <brief-status v-if="!isNew" :brief="brief" />
                        </g-level-item>
                    </g-level>
                    <g-level v-if="rowDate" class="brief-date">
                        <g-typography tag="div" :value="getCustomizableTitle(rowDate.label)" />
                        <g-typography class="row-date" tag="div" :value="formatDate(rowDate.date)" />
                    </g-level>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import get from 'lodash/get';
    import {
        ACTIVATION_BRIEF_STATUS_NONE,
        ACTIVATION_BRIEF_STATUS_IN_REVIEW,
        ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL,
        ACTIVATION_BRIEF_STATUS_ACCEPTED,
        ACTIVATION_BRIEF_STATUS_DECLINED
    } from '../../../../app-v2/constants/activation-brief';
    import BriefStatus from '../../../../app-v2/components/library/BriefStatus.vue';
    import { getActivationCustomizableTitle } from '../../../../app-v2/helpers/activations/default-texts';
    import moment from 'moment-timezone';

    export default {
        name: 'ActivationListRow',

        components: {
            BriefStatus
        },

        props: {
            type : {
                type: String,
                required: true
            },
            object: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                showFullDescription: false
            };
        },

        computed: {
            isTypeActivation() {
                return this.type === 'activation';
            },
            isTypeActivationBrief() {
                return this.type === 'activation_brief';
            },
            activation() {
                return this.isTypeActivation ? this.object : get(this.object, 'activation');
            },
            brief() {
                // If this is an activation, use is_newest to ge the brief
                return this.isTypeActivationBrief
                    ? this.object
                    : get(this.object, 'briefs', []).find((brief) => brief.is_newest === true);
            },
            brandName() {
                return get(this.activation, 'brand.name', '');
            },
            activationImage() {
                // Default to a short but valid data URI to prevent broken images
                const defaultURI = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

                return get(this.activation, 'image', defaultURI) ?? defaultURI;
            },
            isActivationReoccurring() {
                return get(this.activation, 'is_reoccurring', false);
            },
            isNew() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_NONE;
            },
            isPendingApproval() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL;
            },
            isDeclined() {
                return get(this.brief, 'status') === ACTIVATION_BRIEF_STATUS_DECLINED;
            },
            briefProposalEnd() {
                return get(this.activation, 'brief_submission_end_at');
            },
            descriptionClass() {
                return this.showFullDescription ? '' : 'preview-div';
            },
            continueRoute() {
                if (this.isNew || this.isPendingApproval) {
                    return {
                        name: 'v2.activation.brief',
                        params: { brief_id: this.brief.id }
                    };
                }
                return {
                    name: 'v2.activation.brief.overview',
                    params: { brief_id: this.brief.id }
                };
            },
            continueText() {
                return this.isNew ? 'Join Campaign' : 'View Campaign';
            },
            rowDate() {
                const status = get(this.brief, 'status');

                if (!status) {
                    return null;
                }

                if (status === ACTIVATION_BRIEF_STATUS_NONE) {
                    return this.getLabeledDate(
                        this.activation.brief_submission_end_at,
                        'campaign_proposal_dates_header'
                    );
                }
                if (status === ACTIVATION_BRIEF_STATUS_IN_REVIEW) {
                    return this.getLabeledDate(this.brief.submitted_at, 'campaign_proposal_submitted_date_header');
                }
                if (status === ACTIVATION_BRIEF_STATUS_ACCEPTED) {
                    if (this.brief.content_tracking_end_at) {
                        return this.getLabeledDate(this.brief.content_tracking_end_at, 'campaign_content_dates_header');
                    }
                    return this.getLabeledDate(
                        this.brief.reviewed_at || this.brief.accepted_at,
                        'campaign_proposal_accepted_date_header'
                    );
                }
                if (status === ACTIVATION_BRIEF_STATUS_DECLINED) {
                    return this.getLabeledDate(this.brief.declined_at, 'campaign_proposal_declined_date_header');
                }

                return null;
            }
        },

        methods: {
            getCustomizableTitle(key) {
                return getActivationCustomizableTitle(this.activation, key);
            },
            formatDate(isoDate) {
                return moment(isoDate).format('MMM D, YYYY');
            },
            getLabeledDate(date, label) {
                if (!date) {
                    return null;
                }

                return {
                    date,
                    label
                };
            },
            toggleShowDescription() {
                this.showFullDescription = !this.showFullDescription;
            },
            goToActivation() {
                if (this.isDeclined) {
                    return;
                }
                this.$router.push(this.continueRoute);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .break-new-line {
        white-space: pre-line;
    }
    .row-date {
        margin-left: 4px;
    }
    .brief-date {
        margin-top: 4px;
    }
    .preview-div {
        height: 60px;
        overflow: hidden;
        cursor: pointer;
    }
    .activation-image {
        object-fit: cover;
        background-color: $g-color-grey-2;
    }
</style>
